import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import PageHeader from '../components/PageHeader';
import { publishedIntegrations, integrationCategories } from '../content/integrations';

export default function IntegrationsIndex() {
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';
  const page = t('integrations_hub', { returnObjects: true });

  // Group published integrations by category, preserving category order.
  const grouped = integrationCategories
    .map((cat) => ({
      cat,
      items: publishedIntegrations.filter((i) => i.category === cat.id),
    }))
    .filter((g) => g.items.length > 0);

  const catLabel = (cat) => (i18n.language === 'et' ? cat.et : cat.en);

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO title={page.title} description={page.meta_desc} />
      <SchemaMarkup type="collection" data={{ title: page.title, description: page.meta_desc, url: `${langPrefix}/integrations` }} />
      <PageHeader title={page.hero_title} subtitle={page.hero_subtitle} maxWidth="max-w-6xl" />

      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl space-y-12">
        {grouped.map(({ cat, items }) => (
          <section key={cat.id}>
            <h2 className="text-xs font-bold text-blue-700 uppercase tracking-widest mb-5">
              {catLabel(cat)}
            </h2>
            <div className="grid gap-5 sm:grid-cols-2">
              {items.map((item) => {
                const card = t(item.translationKey, { returnObjects: true });
                return (
                  <Link
                    key={item.slug}
                    to={`${langPrefix}/integrations/${item.slug}`}
                    className="group bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {card.card_title || card.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-4">{card.card_desc}</p>
                    <span className="inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:text-blue-500 transition-colors">
                      {page.card_link} <ArrowRightIcon className="w-4 h-4" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

        {/* CTA */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-12 border border-slate-800 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{page.cta_title}</h2>
          <p className="text-slate-300 mb-8">{page.cta_desc}</p>
          <Link
            to={`${langPrefix}/contact`}
            className="inline-flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-full font-bold shadow-lg hover:bg-blue-500 hover:scale-105 transition-all duration-300"
          >
            {page.cta_btn} <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
